import { minmax } from "frontend/geometry/utils";
import { clamp, IRect, Point } from "frontend/utils/math-utils";
import { equals, keys, values } from "rambda";
import { TypeTableCell, TypeTableElement } from "shared/datamodel/schemas/table";
import { TableIds } from "./table-utils";
import { TableSelectionState } from "state-atoms";
import BoundingBox from "frontend/geometry/bounding-box";
import { initArray } from "utils/fn-utils";

export interface CellKey {
  col: string;
  row: string;
}

function toggleCellFromListInPlace(cell: CellKey, cells: CellKey[]): CellKey[] {
  const index = cells.findIndex(equals(cell));
  if (index == -1) cells.push(cell);
  else cells.splice(index, 1);
  return cells;
}

interface ITableStructure {
  getBoundsAt(colNumber: number, rowNumber: number): null | IRect;
  getColNumber(col: string): number;
  getRowNumber(row: string): number;
}

//Todo: this TableStructure class can replace and simplify a lot of code right now.
export class TableStructure implements ITableStructure {
  private _grid?: (null | IRect)[][];

  constructor(
    readonly tableId: string,
    private readonly element: TypeTableElement,
    readonly cells: Record<string, TypeTableCell>
  ) {}

  #buildBoundsGrid(): (null | IRect)[][] {
    const cols = this.element.cols.length;
    const rows = this.element.rows.length;
    let grid = initArray(cols, () => new Array(rows).fill(null));
    const mergeRoots = new Set<string>();
    for (const cell of Object.values(this.cells)) {
      if (cell?.mergedTo) {
        mergeRoots.add(cell?.mergedTo);
      }
    }
    const idMaker = new TableIds(this.tableId);
    for (const root of mergeRoots) {
      // get full id from short-id
      let fullId = idMaker.longIdFromMergedTo(root);
      let rootCell = this.cells[fullId];
      if (!rootCell) {
        console.error("can't find the root of a merged cell.");
        continue;
      }
      // translate col,row to x,y
      const { spanX = 1, spanY = 1 } = rootCell;
      const { col, row } = TableIds.getColAndRow(root);
      let x = Math.max(0, this.getColNumber(col)),
        y = Math.max(this.getRowNumber(row)),
        width = spanX,
        height = spanY;
      // clamp so we don't accidentaly overflow the table (never supposed to happen, very defensive here)
      if (width + x >= cols) width = cols - x;
      if (height >= rows) height = rows - y;

      // and put the bounds object inside the merged cells
      const rect: IRect = { x, y, width: spanX, height: spanY };
      for (let xx = x; xx < x + width; xx++)
        for (let yy = y; yy < y + height; yy++) {
          grid[xx][yy] = rect;
        }
    }
    return grid;
  }

  // grid is initialized only on demand.
  private get grid(): (null | IRect)[][] {
    return (this._grid ??= this.#buildBoundsGrid());
  }

  // todo: can optimize if I build lookup table (id -> number) in constructor
  getColNumber(col: string): number {
    return this.element.cols.findIndex(({ id }) => id === col);
  }
  getRowNumber(row: string): number {
    return this.element.rows.findIndex(({ id }) => id === row);
  }
  getBoundsAt(colNumber: number, rowNumber: number): null | IRect {
    return this.grid[colNumber][rowNumber];
  }
}

export interface ITableSelectionHelper {
  readonly empty: boolean;
  readonly cursor: { col: string; row: string } | null;
  readonly size: number;
  isSelected(cell: CellKey): boolean;
  cells(): Generator<CellKey>;
  getFullySelectedColumns(): null | string[];
  getFullySelectedRows(): null | string[];
  toggle(colNumber: number, rowNumber: number): TableSelectionState; // toggle selection for single cell
  rectTo(colNumber: number, rowNumber: number, structure: ITableStructure): TableSelectionState; // select recatngle from existing anchor to new position
  rect(p1: CellKey, colNumber: number, rowNumber: number, structure: ITableStructure): TableSelectionState; // select a rectangle of cells
  single(colNumber: number, rowNumber: number): TableSelectionState; // select a single cell
  row(rowNumber: number, toggle: boolean): TableSelectionState; // toggle/select entire row
  col(colNumber: number, toggle: boolean): TableSelectionState; // toggle/select entire column
  moveCursor(dx: number, dy: number, cells: Record<string, TypeTableCell>): TableSelectionState | null;
  unsafe_add(cells: CellKey[]): TableSelectionState;
}

export function makeSelectionHelper(tableId: string, element: TypeTableElement, state: TableSelectionState | null) {
  const empty = state == null || !state.cells.length;
  return empty ? new EmptySelection(tableId, element) : new TableSelectionHelper(tableId, element, state);
}

abstract class SelectionBase implements ITableSelectionHelper {
  constructor(
    readonly tableId: string,
    readonly element: TypeTableElement,
    public anchor: CellKey | null,
    readonly selection: CellKey[] | null
  ) {}

  abstract get size(): number;
  abstract get empty(): boolean;
  abstract get cursor(): { col: string; row: string } | null;
  abstract isSelected(cell: CellKey): boolean;
  abstract cells(): Generator<CellKey>;
  abstract getFullySelectedColumns(): null | string[];
  abstract getFullySelectedRows(): null | string[];

  toggle(colNumber: number, rowNumber: number): TableSelectionState {
    const col = this.element.cols[colNumber].id;
    const row = this.element.rows[rowNumber].id;
    const newCells = toggleCellFromListInPlace({ col, row }, this.selection ? [...this.selection] : []);
    return { id: this.tableId, anchor: this.anchor || { col, row }, cells: newCells };
  }

  rectTo(colNumber: number, rowNumber: number, structure: ITableStructure): TableSelectionState {
    const { cols, rows } = this.element;
    const col = cols[colNumber].id;
    const row = rows[rowNumber].id;
    const p2 = { col, row };
    const p1 = this.anchor || p2;
    // find the numeric index of start and end col/row keys
    let x1 = -1,
      x2 = -1,
      y1 = -1,
      y2 = -1;
    for (let i = 0; i < cols.length; i++) {
      if (cols[i].id == p1.col) x1 = i;
      if (cols[i].id == p2.col) x2 = i;
    }
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].id == p1.row) y1 = i;
      if (rows[i].id == p2.row) y2 = i;
    }
    // if not found, return
    if (x1 == -1 || y1 == -1 || x2 == -1 || x1 == -1) {
      throw new Error("can't find start/end of rectangle in table");
    }
    // make sure x1<x2 and y1<y2
    [x1, x2] = minmax(x1, x2);
    [y1, y2] = minmax(y1, y2);

    // let's check if we're including merged cells in our rectangle. If we do, they push the boundary
    // of the rect so it covers them completely.
    // This has to go over and over again until nothing changes..
    // I'll also limit to loop to 100 iterations, just in case we have a monster table
    for (let limit = 0; limit < 100; limit++) {
      let changed = false;
      for (let x = x1; x <= x2; x++) {
        for (let y = y1; y <= y2; y++) {
          let bounds = structure.getBoundsAt(x, y);
          if (bounds) {
            x1 = Math.min(x1, bounds.x);
            y1 = Math.min(y1, bounds.y);
            x2 = Math.max(x2, bounds.x + bounds.width - 1);
            y2 = Math.max(y2, bounds.y + bounds.height - 1);
            changed = true;
          }
        }
      }
      if (!changed) break;
    }

    // collect all values in the rectangle (start x,y)..(end x,y)
    const newSelection = [];
    for (let col = x1; col <= x2; col++) {
      for (let row = y1; row <= y2; row++) {
        newSelection.push({ col: cols[col].id, row: rows[row].id });
      }
    }
    // check for merged cells: if we select a merged cell, it will extend the selection-rect (just like excel, google sheets)
    // in other words, the rect is expanded until all cells originally in it are fully included (and not bulging out)

    // now make sure that the final cell in selection (aka cursor) is where the user moved to ({col,row})
    // todo: in case of merged cells extending the selection, the cursor should jump to the corner of the new selection-rect
    // that most closely matches user's intentions
    let cursor = { col, row };
    let i = newSelection.findIndex(equals(cursor));
    newSelection[i] = newSelection[newSelection.length - 1];
    newSelection[newSelection.length - 1] = cursor;
    return { id: this.tableId, anchor: p1, cells: newSelection };
  }

  rect(p1: CellKey, colNumber: number, rowNumber: number, structure: ITableStructure): TableSelectionState {
    this.anchor = p1;
    return this.rectTo(colNumber, rowNumber, structure);
  }

  single(colNumber: number, rowNumber: number): TableSelectionState {
    const col = this.element.cols[colNumber].id;
    const row = this.element.rows[rowNumber].id;
    const p = { col, row };
    return { id: this.tableId, anchor: p, cells: [p] };
  }

  row(rowNumber: number, toggle: boolean): TableSelectionState {
    const row = this.element.rows[rowNumber].id;
    let cells;
    // in case of toggle==false (but also if selection is empty initially), we select the single row we're given
    if (!toggle || !this.selection?.length) {
      cells = this.element.cols.map(({ id: col }) => ({ col, row }));
    } else {
      cells = this.selection.slice(); // duplicate - never change data passed to us from outside
      for (const { id: col } of this.element.cols) {
        const cell = { col, row };
        toggleCellFromListInPlace(cell, cells);
      }
    }
    return { id: this.tableId, anchor: this.anchor!, cells };
  }

  col(colNumber: number, toggle: boolean): TableSelectionState {
    const col = this.element.cols[colNumber].id;
    let cells;
    // in case of toggle==false (but also if selection is empty initially), we select the single row we're given
    if (!toggle || !this.selection?.length) {
      cells = this.element.rows.map(({ id: row }) => ({ col, row }));
    } else {
      cells = this.selection.slice(); // duplicate - never change data passed to us from outside
      for (const { id: row } of this.element.rows) {
        const cell = { col, row };
        toggleCellFromListInPlace(cell, cells);
      }
    }
    return { id: this.tableId, anchor: this.anchor!, cells };
  }

  moveCursor(dx: number, dy: number, cells: Record<string, TypeTableCell>): TableSelectionState {
    try {
      if (this.cursor) {
        const { x, y } = move(this.cursor, dx, dy, this.element, cells);
        const col = this.element.cols[x].id;
        const row = this.element.rows[y].id;
        return { id: this.tableId, anchor: { col, row }, cells: [{ col, row }] };
      }
    } catch {
      // ignore error - the next section returns sensible value
    }
    // if move failed or no cursor, reset selection
    const col = this.element.cols[0].id;
    const row = this.element.rows[0].id;
    return { id: this.tableId, anchor: { col, row }, cells: [{ col, row }] };
  }

  unsafe_add(cells: CellKey[]): TableSelectionState {
    if (this.selection == null || this.selection.length == 0) {
      return { id: this.tableId, anchor: cells[0], cells };
    } else {
      let temp = this.selection.slice();
      for (const cell of cells) {
        if (temp.indexOf(cell) == -1) {
          temp.push(cell);
        }
      }
      return { id: this.tableId, anchor: cells[0], cells: temp };
    }
  }
}

class EmptySelection extends SelectionBase implements ITableSelectionHelper {
  public readonly empty = true;
  public readonly cursor = null;
  public readonly size = 0;
  constructor(tableId: string, element: TypeTableElement) {
    super(tableId, element, null, []);
  }

  isSelected(_cell: CellKey): boolean {
    return false;
  }
  // eslint-disable-next-line require-yield
  *cells(): Generator<CellKey> {
    return;
  }
  getFullySelectedColumns(): null | string[] {
    return null;
  }
  getFullySelectedRows(): null | string[] {
    return null;
  }
}

export class TableSelectionHelper extends SelectionBase implements ITableSelectionHelper {
  constructor(tableId: string, element: TypeTableElement, selection: TableSelectionState) {
    super(tableId, element, selection.anchor, selection.cells);
  }

  public readonly empty = false;

  get size(): number {
    return this.selection?.length ?? 0;
  }
  get cursor(): { col: string; row: string } | null {
    if (this.selection?.length) {
      return this.selection[this.selection.length - 1];
    }
    return null;
  }

  #indexCellInSelection(col: string, row: string): number {
    return this.selection?.findIndex((a) => a.col == col && a.row == row) ?? -1;
  }
  isSelected(cell: CellKey): boolean {
    return this.#indexCellInSelection(cell.col, cell.row) != -1;
  }

  *cells() {
    if (!this.selection) return; // should never happen
    yield* this.selection;
  }

  // returns keys of columns - only if selection is entirely those columns and nothing else
  getFullySelectedColumns(): null | string[] {
    if (this.empty) return null; // should never happen
    // ...implementation...
    // Count how many cells are selected in each column
    // then check if that's the entire length of the table
    const count = {} as Record<string, number>;
    for (const { col } of this.selection!) {
      count[col] = (count[col] ?? 0) + 1;
    }
    const numRows = this.element.rows.length;
    if (values(count).every(equals(numRows))) return keys(count);
    return null;
  }

  // returns keys of rows - only if selection is entirely those rows and nothing else
  getFullySelectedRows(): null | string[] {
    if (this.empty) return null; // should never happen
    // ...implementation...
    // count how many cells are selected in each row, and return row keys
    // if for every row with selected cells - it's entirely selected.
    const count = {} as Record<string, number>;
    for (const { row } of this.selection!) {
      count[row] = (count[row] ?? 0) + 1;
    }
    const numCols = this.element.cols.length;
    if (values(count).every(equals(numCols))) return keys(count);
    return null;
  }
}

/**
 * This function is for calculating movement of cursor in a grid containing merged cells
 * Movement within a grid with merged cells should behave as follows:
 * dx,dy are always +1 or -1 or 0
 * the cursor should move to the next cell in the direction of dx,dy
 * if the cursor is inside a merged-cell when it starts, the next step must be outside of the merged
 * cell. if not, the movement is single-step
 * cursor is limited to grid legal coordinates at all times
 *
 * @param cursor - the starting position
 * @param dx - ±1
 * @param dy - ±1
 * @param element - contains cols and rows
 * @param cells - object containing cells data
 * @returns
 */
export function move(
  cursor: CellKey,
  dx: number,
  dy: number,
  element: TypeTableElement,
  cells: Record<string, TypeTableCell>
): Point {
  const srcX = element.cols.findIndex(({ id }: { id: string }) => id == cursor.col);
  const srcY = element.rows.findIndex(({ id }: { id: string }) => id == cursor.row);
  // It's possible that cursor was on a cell that another user deleted (table structure can change under us!)
  // in that case the cursor is invalid. I can save cursor as (x,y) point, not as (col,row), and then clamp to limits...
  // or I can just reset cursor and selection, or throw exception and let caller to decide
  if (srcX == -1 || srcY == -1) {
    throw new Error();
  }
  let destX = srcX + dx;
  let destY = srcY + dy;
  if (destX < 0 || destY < 0 || destX >= element.cols.length || destY >= element.rows.length) {
    destX = clamp(destX, 0, element.cols.length - 1);
    destY = clamp(destY, 0, element.rows.length - 1);
    return { x: destX, y: destY };
  }

  const cellArray = buildCellsArray(element, cells);
  const src = cellArray.get(srcX, srcY);
  if (!src) {
    // I don't think it's possible to get here, but just in case.
    throw new Error();
  } else {
    // check if we are still inside a merged cell, and if so, move until we get out of it or stop at table bounds
    let dest = cellArray.get(destX, destY);
    if (src != dest && src.mergedTo && dest.mergedTo == src.mergedTo) {
      while (dest.mergedTo == src.mergedTo) {
        destX += dx;
        destY += dy;
        if (destX < 0 || destY < 0 || destX >= element.cols.length || destY >= element.rows.length) {
          break;
        }
        dest = cellArray.get(destX, destY);
      }
    }
  }
  destX = clamp(destX, 0, element.cols.length - 1);
  destY = clamp(destY, 0, element.rows.length - 1);
  return { x: destX, y: destY };
}

// convert the object of all cells to a 1d array
function buildCellsArray(element: TypeTableElement, cells: Record<string, TypeTableCell>) {
  const col_ids = element.cols.map(({ id }) => id);
  const row_ids = element.rows.map(({ id }) => id);
  const cellsArray = new Array(col_ids.length * row_ids.length);
  for (const id in cells) {
    const { col, row } = TableIds.getColAndRow(id);
    const x = col_ids.indexOf(col);
    const y = row_ids.indexOf(row);
    if (x >= 0 && y >= 0) {
      cellsArray[y * col_ids.length + x] = cells[id];
    }
  }
  return {
    get(col: number, row: number) {
      return cellsArray[row * col_ids.length + col];
    },
  };
}

/**
 * Check if a group of selected cells form a rectangle: a single, contiguous area, equals sides
 * where all cells are in the selected group.
 * edge cases
 * - empty set and 1-element set are not considered rectangles.
 * - merged cells complicate things
 * Algorithm (for simple cases):
 *  find minimum and maximum x,y in the selected set -> compute width,height
 *  check that width*height = length(valid-selection)
 *  where valid-selection is the valid (in table) cells in the selection=
 */
export function isRectangle(
  selection: CellKey[],
  element: TypeTableElement,
  tableId: string,
  cells: Record<any, { mergedTo?: string; spanX?: number; spanY?: number }>
): BoundingBox | null {
  // empty selection and single-cell selection isn't considered rectangle
  if (!selection || selection.length <= 1) return null;

  // Build lookup for col/row indices (not efficient for very large tables, but I assume ours are small)
  const colIds = element.cols.map((c) => c.id);
  const rowIds = element.rows.map((r) => r.id);
  const b = new BoundingBox();
  let valid = 0;
  const keys = new TableIds(tableId);
  let mergeRoots = new Set<string>();
  for (const { col, row } of selection) {
    const x = colIds.indexOf(col);
    const y = rowIds.indexOf(row);
    if (x === -1 || y === -1) continue; // invalid cell - can happen if another user changes table structure while we have selection
    const id = keys.longId(col, row);
    if (cells[id]?.mergedTo) {
      mergeRoots.add(cells[id].mergedTo as unknown as string);
    } else {
      valid++;
      b.expandPoint(x, y);
    }
  }
  // special check for merged cells:
  // if in the selection there is a merged-cell, we take its entirety into account:
  // that means go to merge root and find the size (from spanX and spanY)
  // valid count = valid +  (spanX*spanY)
  // bounds increases accordingly
  for (const mergeRoot of mergeRoots) {
    const cell = cells[keys.longIdFromMergedTo(mergeRoot)];
    const { col, row } = TableIds.getColAndRow(mergeRoot);
    const { spanX = 1, spanY = 1 } = cell;
    valid += spanX * spanY; // the entire merged-cell is considered selected
    const x = colIds.indexOf(col);
    const y = rowIds.indexOf(row);
    b.expandPoint(x, y);
    b.expandPoint(x + spanX - 1, y + spanY - 1);
  }
  const m = b.width + 1;
  const n = b.height + 1;

  return m * n === valid ? b : null;
}
